// Dependencies
import { forEach } from '../polyfill/foreach.js';
import { BREAKPOINT } from '../constants';
import { debounce } from '../utils/debounce.js';

/**
 * Use on parent element by adding a 'data-js-more' attribute. Customise with attributes.
 * @class More
 */
class More {
  /**
   * Creates an instance of More.
   * @param {HTMLElement} el
   * @memberof More
   */
  constructor(el) {
    this.el = el;
    this.expanded = false;
    this.clipped = false;
    this.dataArgs = this.el.getAttribute('data-js-more');
    this._mobileOnly = this.el.hasAttribute('data-js-more-mobile');
    this._wpReadMore = this.el.querySelector('[id^=more]');

    // Default arguments
    this._args = {
      count: 1,
      class: 'btn--more',
      manual: false,
      text: 'More',
      collapse: true,
    };

    // If element has data attribute with args, update the object
    if (this.dataArgs) {
      this.args = JSON.parse(this.dataArgs);
    }
  }

  /**
   * Get the arguments of the More element
   * @memberof More
   */
  get args() {
    return this._args;
  }

  /**
   * Set the arguments of the More element
   * @memberof More
   */
  set args(newArgs) {
    this._args = Object.assign({}, this._args, newArgs);
  }

  /**
   * Clip element and add More button
   * @memberof More
   */
  _injectButton() {
    let count = 1;

    // Store all elements to clip
    const children = this.el.children;

    // If set to manual and no WP more tag is found, throw error
    if (this.args.manual && !this._wpReadMore) {
      throw 'No read more tag found in WordPress';
    }

    if (this.args.manual && this._wpReadMore) {
      if (this._wpReadMore.nextSibling) {
        // Get parent then prev p tag
        if (
          ['#text', 'a'].indexOf(
            More._getTagName(this._wpReadMore.nextSibling),
          ) >= 0
        ) {
          count += [...this._wpReadMore.parentNode.parentNode.children].indexOf(
            this._wpReadMore.parentNode.previousElementSibling,
          );
        } else {
          count += [...this._wpReadMore.parentNode.children].indexOf(
            this._wpReadMore,
          );
        }
      } else if (this._wpReadMore.previousSibling) {
        // Get parent
        if (
          ['#text', 'a'].indexOf(
            More._getTagName(this._wpReadMore.previousSibling),
          ) >= 0
        ) {
          count += [...this._wpReadMore.parentNode.parentNode.children].indexOf(
            this._wpReadMore.parentNode,
          );
        } else {
          count += [...this._wpReadMore.parentNode.children].indexOf(
            this._wpReadMore,
          );
        }
      }
      // If a child of paragraph tag (and only child), remove margin
      if (
        More._getTagName(this._wpReadMore.parentNode) === 'p' &&
        !this._wpReadMore.nextSibling &&
        !this._wpReadMore.previousSibling
      ) {
        this._wpReadMore.parentNode.style.marginBottom = '0px';
        count += [...this._wpReadMore.parentNode.parentNode.children].indexOf(
          this._wpReadMore.parentNode.previousElementSibling,
        );
      }
    }

    if (!this.args.manual) {
      count = this.args.count;
    }

    // If children is not greater than clip count, throw error
    if (children.length <= count) {
      throw 'Not enough children to clip';
    }

    // Create new HTML element with hidden children
    this.clippedContent = document.createElement('div');

    this.clippedContent.style.display = 'none';

    for (let i = children.length - 1; i >= count; i--) {
      this.clippedContent.insertBefore(
        children[i],
        this.clippedContent.firstChild,
      );
    }

    this.el.appendChild(this.clippedContent);

    this.btn = document.createElement('button');
    this.btn.className = this.args.class;
    this.btn.type = 'button';
    this.btn.textContent = this.args.text;
    this.btn.setAttribute('aria-expanded', 'false');
    this.btn.setAttribute('aria-label', 'Toggle content');

    this.el.appendChild(this.btn);
    this.clipped = true;
  }

  /**
   * Hide the button and show clipped content
   * @private
   * @memberof More
   */
  _unclip() {
    if (!this.clipped) {
      return;
    }

    this.clippedContent.style.display = 'block';
    this.btn.style.display = 'none';
    this.clipped = false;
  }

  /**
   * SHow the button and hide the clipped content
   * @private
   * @memberof More
   */
  _clip() {
    if (this.clipped) {
      return;
    }

    this.clippedContent.style.display = 'none';
    this.btn.style.display = 'block';
    this.btn.textContent = 'More';
    this.btn.setAttribute('aria-expanded', 'false');
    this.clipped = true;
  }

  /**
   * Add event listeners
   * @private
   * @memberof More
   */
  _addEventListeners() {
    // Add click event to more expander button
    this.btn.addEventListener('click', this.toggle.bind(this), false);

    // Resize handler for mobile
    if (this._mobileOnly) {
      window.addEventListener(
        'resize',
        debounce(() => this._handleResize(), 250),
      );
    }
  }

  /**
   * Resize event handler
   * @memberof More
   */
  _handleResize() {
    if (window.innerWidth < BREAKPOINT.MD) {
      this._clip();
    } else {
      this._unclip();
    }
  }

  /**
   * Returns the tag name of an element
   *
   * @param {HTMLElement} el HTML element
   * @memberof More
   * @returns {string} tag name
   */
  static _getTagName(el) {
    return el.nodeName.toLowerCase();
  }

  /**
   * Click handler for More button
   * @memberof More
   */
  toggle() {
    if (!this.expanded) {
      jQuery(this.clippedContent).slideDown('fast');
      this.clippedContent.tabIndex = -1;
      this.clippedContent.focus();

      if (!this.args.collapse) {
        this.btn.parentNode.removeChild(this.btn);
      }

      this.btn.setAttribute('aria-expanded', 'true');
      this.btn.textContent = 'Less';
      this.expanded = true;
    } else {
      jQuery(this.clippedContent).slideUp('fast');
      this.btn.setAttribute('aria-expanded', 'false');
      this.btn.textContent = this.args.text;
      this.expanded = false;
    }
  }

  /**
   * Setup element and add event listeners
   * @memberof More
   */
  init() {
    // Clip HTML and insert more button
    try {
      this._injectButton();
    } catch (err) {
      return;
    }

    // Add event listeners
    this._addEventListeners();

    if (this._mobileOnly) {
      this._handleResize();
    }
  }
}

export function init() {
  const els = document.querySelectorAll('[data-js-more]');

  forEach(els, (index, el) => {
    const more = new More(el);

    more.init();
  });
}
